import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Check, Cloud, CloudOff, History, Layers, Palette, Redo2, Undo2, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { useAuth } from '@/app/providers';
import { useAuthModal } from '@/features/auth/authModalStore';
import { cn } from '@/lib/utils';
import { setCustomTheme, setTheme } from '@/features/theme/themes';
import { useCommunity } from '@/features/communities/hooks/useCommunities';
import { useChannels } from '@/features/communities/hooks/useChannels';
import { useMembers, useMessages } from '@/features/communities/hooks/useCommunityView';
import type { Channel } from '@/features/communities/community-view/channels';
import { loadStudioDraft, type StudioChannel, type StudioState } from './studioModel';
import { docOf } from './api/themes';
import { uploadThemeAsset } from './api/assets';
import { migrateDoc, migrateLegacyDraft } from './model/doc';
import { emptyBannerDesign, newLayer, type BannerEditing, type BannerLayer } from './model/bannerLayers';
import { flatFromDoc, useEditorStore } from './state/editorStore';
import { useAutosave } from './hooks/useAutosave';
import { useCommunityDraft, useThemeRow, themeKeys } from './hooks/useThemeDoc';
import { useStudioShortcuts } from './hooks/useStudioShortcuts';
import { StudioLayers } from './StudioLayers';
import { StudioCanvas } from './StudioCanvas';
import { StudioInspector } from './StudioInspector';
import { StudioCommunitySwitcher } from './StudioCommunitySwitcher';
import { StudioToolbar } from './StudioToolbar';
import { PublishDialog } from './chrome/PublishDialog';
import { StudioHistoryPanel } from './chrome/StudioHistoryPanel';
import { PresetGallery } from './chrome/PresetGallery';
import { StudioSectionNav } from './chrome/StudioSectionNav';
import { LayerPanel } from './chrome/LayerPanel';
import { AssetsPanel } from './chrome/AssetsPanel';
import { BannerContextStrip, COMMUNITY_BANNER_CONTEXTS } from './chrome/BannerContextStrip';
import { BannerLayerCanvas } from './canvas/BannerLayerCanvas';
import { LayerInspector } from './inspector/LayerInspector';

/** Best-effort icon id for a real community channel (mirrors the rail's glyphs). */
function iconIdForChannel(ch: Channel): string {
  if (ch.kind === 'voice') return 'volume';
  switch (ch.id) {
    case 'announcements':
      return 'megaphone';
    case 'wins':
      return 'trophy';
    case 'intros':
    case 'rules':
      return 'lock';
    case 'off-topic':
      return 'sparkles';
    case 'general':
      return 'message';
    default:
      return 'hash';
  }
}

const toStudioChannel = (ch: Channel): StudioChannel => ({
  id: ch.id,
  name: ch.name,
  icon: ch.icon ?? iconIdForChannel(ch),
  kind: ch.kind,
  section: ch.section || (ch.kind === 'voice' ? 'VOICE CHANNELS' : 'TEXT CHANNELS'),
  effect: (ch.effect as StudioChannel['effect']) ?? 'none',
});

/**
 * Outer page — remounts the editor whenever the document scope changes
 * (community route param or `?t=` theme id), so switching documents fully
 * reloads that document's real data.
 */
export function ThemeStudioPage() {
  const { communityId } = useParams<{ communityId?: string }>();
  const [params] = useSearchParams();
  const themeParam = params.get('t') ?? undefined;
  return <Studio key={communityId ?? themeParam ?? 'blank'} communityId={communityId} themeParam={themeParam} />;
}

/**
 * Theme Studio — the full-screen editor over a server-persisted Theme Document.
 * `/studio` edits a personal doc (`?t=<id>` addresses a specific one, created
 * lazily on first edit); `/studio/:communityId` edits the community's draft,
 * seeded from its published theme or its live identity. Autosave writes the doc
 * ~2s after every edit; Publish snapshots it to the community so every member
 * sees it. Signed-out users keep a local-only playground with a sign-in upsell.
 */
function Studio({ communityId, themeParam }: { communityId?: string; themeParam?: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const openAuth = useAuthModal((s) => s.openAuth);

  const { data: community } = useCommunity(communityId ?? '');
  const isOwner = Boolean(communityId && community && user && community.owner_id === user.id);
  const { data: liveChannels } = useChannels(communityId ?? '', community?.owner_id);
  const { data: members = [] } = useMembers(communityId ?? '', community?.owner_id ?? null);
  const { data: messages = [] } = useMessages(communityId ?? '');
  const live = communityId
    ? { members, messages, memberCount: community?.member_seq ?? members.length }
    : null;

  // Document row: a community draft (owners), or an addressed personal doc.
  const draftQuery = useCommunityDraft(isOwner ? communityId : undefined, user?.id);
  const rowQuery = useThemeRow(!communityId ? themeParam : undefined);
  const loadedRow = communityId ? draftQuery.data : rowQuery.data;

  // Editor store
  const flat = useEditorStore((s) => s.flat);
  const selection = useEditorStore((s) => s.selection);
  const mode = useEditorStore((s) => s.mode);
  const zoom = useEditorStore((s) => s.zoom);
  const dirty = useEditorStore((s) => s.dirty);
  const saving = useEditorStore((s) => s.saving);
  const saveError = useEditorStore((s) => s.saveError);
  const lastSavedAt = useEditorStore((s) => s.lastSavedAt);
  const themeRowId = useEditorStore((s) => s.themeRowId);
  const themeName = useEditorStore((s) => s.themeName);
  const editSeq = useEditorStore((s) => s.editSeq);
  const canUndo = useEditorStore((s) => s.past.length > 0);
  const canRedo = useEditorStore((s) => s.future.length > 0);
  const store = useEditorStore;

  const [publishOpen, setPublishOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const [assetsOpen, setAssetsOpen] = useState(false);
  const seeded = useRef(false);

  // Ready when the data the seed needs has arrived for this scope.
  const ready = communityId
    ? Boolean(community) && liveChannels != null && (!isOwner || draftQuery.isFetched)
    : themeParam
      ? rowQuery.isFetched
      : true;

  useEffect(() => {
    if (!ready || seeded.current) return;
    seeded.current = true;

    const legacy = !loadedRow && typeof window !== 'undefined' ? loadStudioDraft(communityId) : null;
    const doc = loadedRow
      ? docOf(loadedRow)
      : communityId && community?.theme_doc
        ? migrateDoc(community.theme_doc)
        : legacy
          ? migrateLegacyDraft(legacy)
          : migrateLegacyDraft({});

    // Communities without a theme yet seed brand fields from the live row.
    if (communityId && community && !loadedRow && !community.theme_doc) {
      if (community.color) doc.accent = community.color;
      if (community.banner && !community.banner.startsWith('data:')) {
        doc.identity.banner = { url: community.banner, focal: { x: 50, y: 50 }, design: null };
      }
    }

    store.getState().load({
      flat: flatFromDoc(doc, {
        communityName: community?.name,
        tagline: community?.tagline ?? community?.description ?? undefined,
        channels: communityId ? (liveChannels ?? []).map(toStudioChannel) : undefined,
      }),
      themeRowId: loadedRow?.id ?? null,
      themeName: loadedRow?.name ?? (community ? `${community.name} theme` : 'Untitled theme'),
    });
  }, [ready, loadedRow, community, communityId, liveChannels, store]);

  // Autosave (owners + personal docs; guests and non-owners stay local-only).
  const canPersist = Boolean(user) && (!communityId || isOwner);
  const { flush } = useAutosave({
    userId: canPersist ? (user?.id ?? null) : null,
    communityId: isOwner ? communityId : undefined,
    communityName: community?.name,
    rowUpdatedAt: loadedRow?.updated_at ?? null,
    onRowCreated: (id) => {
      if (!communityId) navigate(`${ROUTES.themeStudio}?t=${id}`, { replace: true });
      void qc.invalidateQueries({ queryKey: themeKeys.mine });
    },
  });

  const update = (patch: Partial<StudioState>, key?: string) => store.getState().patch(patch, key);

  // Store-agnostic accessors for the shared banner workbench (canvas/layers/inspector).
  const bannerLayers = flat.bannerDesign?.layers ?? [];
  const withLayers = (layers: BannerLayer[]) => ({
    bannerDesign: { ...(flat.bannerDesign ?? emptyBannerDesign()), layers },
  });
  const editing: BannerEditing = {
    layers: bannerLayers,
    selectedId: selectedLayerId,
    onSelect: setSelectedLayerId,
    onPatchLayer: (id, patch, key) =>
      update(withLayers(bannerLayers.map((l) => (l.id === id ? ({ ...l, ...patch } as BannerLayer) : l))), key),
    onSetLayers: (layers) => update(withLayers(layers)),
  };

  /** Assets pick → fill the selected empty image layer, else add a new one. */
  function placeImage(url: string): void {
    const layers = store.getState().flat.bannerDesign?.layers ?? [];
    const selected = layers.find((l) => l.id === selectedLayerId);
    if (selected?.kind === 'image' && !selected.url) {
      editing.onPatchLayer(selected.id, { url });
    } else {
      const layer = newLayer('image', { url });
      update(withLayers([...layers, layer]));
      setSelectedLayerId(layer.id);
    }
  }

  async function uploadImage(file: File, kind: 'background' | 'banner'): Promise<void> {
    const patchFor = (url: string) =>
      kind === 'background' ? { themeId: 'custom', customImage: url } : { banner: url };
    if (!user) {
      // Guest: local preview only (object URL — never persisted anywhere).
      update(patchFor(URL.createObjectURL(file)));
      return;
    }
    const preview = URL.createObjectURL(file);
    update(patchFor(preview));
    try {
      const { url } = await uploadThemeAsset(file, kind, user.id);
      update(patchFor(url));
    } catch (e) {
      update(kind === 'background' ? { themeId: 'framer', customImage: null } : { banner: null });
      toast((e as Error).message || 'Upload failed', 'error');
    } finally {
      URL.revokeObjectURL(preview);
    }
  }

  const exit = () =>
    navigate(communityId ? ROUTES.community(communityId) : ROUTES.marketplace, { replace: true });

  /** Apply the doc's background as the viewer's personal skin (blank-scope nicety). */
  function applyAsMySkin() {
    if (flat.themeId === 'custom' && flat.customImage) setCustomTheme(flat.customImage);
    else setTheme(flat.themeId);
    toast('Applied as your background', 'ok');
  }

  useStudioShortcuts({ onFlush: () => void flush(), onExit: exit });

  const pristineBlank = !communityId && !themeParam && editSeq === 0 && !themeRowId;

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-ink-950 text-hi">
      <header className="flex flex-none items-center justify-between gap-3 border-b border-white/[0.08] bg-ink-900 px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={exit}
            aria-label="Exit studio"
            className="grid h-8 w-8 flex-none place-items-center rounded-lg text-mid transition-colors hover:bg-white/10 hover:text-hi"
          >
            <X size={18} />
          </button>
          <span className="flex flex-none items-center gap-2 text-[13px] font-extrabold text-hi">
            <Palette size={16} className="text-accent" />
            <span className="hidden sm:inline">WINR Studio</span>
          </span>
          <span className="text-white/20">/</span>
          <StudioSectionNav active="community" />
          <StudioCommunitySwitcher
            currentId={communityId}
            currentThemeId={themeParam}
            currentLabel={communityId ? flat.communityName : themeName}
          />
          {isOwner && <span className="hidden text-[11px] font-bold text-accent md:inline">● live</span>}
          <SaveIndicator
            signedIn={Boolean(user)}
            canPersist={canPersist}
            saving={saving}
            dirty={dirty}
            error={saveError}
            lastSavedAt={lastSavedAt}
          />
        </div>

        <div className="flex flex-none items-center gap-1.5">
          <HeaderIcon label="Undo (Ctrl+Z)" disabled={!canUndo} onClick={() => store.getState().undo()}>
            <Undo2 size={15} />
          </HeaderIcon>
          <HeaderIcon label="Redo (Ctrl+Shift+Z)" disabled={!canRedo} onClick={() => store.getState().redo()}>
            <Redo2 size={15} />
          </HeaderIcon>
          <HeaderIcon
            label="Version history"
            disabled={!themeRowId}
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <History size={15} />
          </HeaderIcon>
          <span className="mx-1 h-5 w-px bg-white/10" />
          {!communityId && (
            <Button variant="ghost" size="sm" onClick={applyAsMySkin}>
              Apply as my background
            </Button>
          )}
          {user ? (
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!canPersist && Boolean(communityId)}
              title={!canPersist && communityId ? 'Only the owner can publish this community' : undefined}
              onClick={() => {
                void flush().then(() => setPublishOpen(true));
              }}
            >
              <Check size={15} strokeWidth={2.6} />
              {isOwner ? 'Publish to community' : 'Publish'}
            </Button>
          ) : (
            <Button size="sm" onClick={openAuth}>
              Sign in to save
            </Button>
          )}
        </div>
      </header>

      {!ready ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {mode === 'banner' ? (
            <aside className="flex w-60 flex-none flex-col border-r border-white/[0.08] bg-ink-900">
              <div className="flex items-center gap-2 border-b border-white/[0.05] px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.8px] text-white/40">
                <Layers size={12} strokeWidth={2.4} /> Layers
              </div>
              <LayerPanel editing={editing} onAddImage={() => setAssetsOpen(true)} />
            </aside>
          ) : (
            <StudioLayers selected={selection} onSelect={(s) => store.getState().setSelection(s)} />
          )}
          <div className="relative flex min-w-0 flex-1 flex-col">
            <StudioToolbar
              mode={mode}
              onMode={(m) => store.getState().setMode(m)}
              zoom={zoom}
              onZoom={(z) => store.getState().setZoom(z)}
              onOpenAssets={() => setAssetsOpen(true)}
            />
            {mode === 'banner' ? (
              <>
                <BannerContextStrip
                  design={flat.bannerDesign}
                  focal={flat.bannerFocal}
                  onFocal={(bannerFocal) => update({ bannerFocal }, 'focal')}
                  contexts={COMMUNITY_BANNER_CONTEXTS}
                />
                <BannerLayerCanvas editing={editing} />
              </>
            ) : (
              <StudioCanvas
                state={flat}
                selected={selection}
                onSelect={(s) => store.getState().setSelection(s)}
                live={live}
                zoom={zoom}
              />
            )}
            {assetsOpen && <AssetsPanel onClose={() => setAssetsOpen(false)} onPlace={placeImage} />}
            {historyOpen && themeRowId && (
              <StudioHistoryPanel
                themeId={themeRowId}
                onClose={() => setHistoryOpen(false)}
                onRestored={(doc) => {
                  store.getState().load({
                    flat: flatFromDoc(doc, {
                      communityName: flat.communityName,
                      tagline: flat.tagline,
                      channels: flat.channels,
                    }),
                    themeRowId,
                    themeName: store.getState().themeName,
                  });
                  setHistoryOpen(false);
                }}
              />
            )}
          </div>
          {mode === 'banner' ? (
            <aside className="flex w-72 flex-none flex-col overflow-y-auto border-l border-white/[0.08] bg-ink-900">
              <div className="border-b border-white/[0.05] px-4 py-3 text-[10px] font-extrabold uppercase tracking-[0.8px] text-white/40">
                {selectedLayerId ? 'Layer' : 'Banner'}
              </div>
              <LayerInspector editing={editing} />
            </aside>
          ) : (
            <StudioInspector
              state={flat}
              selected={selection}
              onChange={update}
              onUploadImage={(file, kind) => void uploadImage(file, kind)}
            />
          )}
        </div>
      )}

      {pristineBlank && user && <PresetGallery />}

      <PublishDialog
        open={publishOpen}
        onClose={() => setPublishOpen(false)}
        communityId={isOwner ? communityId : undefined}
        communityName={community?.name}
        priorChannels={communityId ? (liveChannels ?? []) : []}
        onFlush={flush}
      />
    </div>
  );
}

function SaveIndicator({
  signedIn,
  canPersist,
  saving,
  dirty,
  error,
  lastSavedAt,
}: {
  signedIn: boolean;
  canPersist: boolean;
  saving: boolean;
  dirty: boolean;
  error: string | null;
  lastSavedAt: number | null;
}) {
  if (!signedIn || !canPersist) {
    return (
      <span className="hidden items-center gap-1.5 text-[11px] font-semibold text-lo lg:flex">
        <CloudOff size={13} /> Local only
      </span>
    );
  }
  if (error) {
    return (
      <span className="hidden items-center gap-1.5 text-[11px] font-semibold text-[#ff8e8e] lg:flex" title={error}>
        <AlertTriangle size={13} /> {error}
      </span>
    );
  }
  return (
    <span
      className={cn(
        'hidden items-center gap-1.5 text-[11px] font-semibold lg:flex',
        saving || dirty ? 'text-mid' : 'text-lo',
      )}
    >
      <Cloud size={13} className={saving ? 'animate-pulse' : undefined} />
      {saving ? 'Saving…' : dirty ? 'Unsaved changes' : lastSavedAt ? 'Saved' : 'Autosave on'}
    </span>
  );
}

function HeaderIcon({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-8 w-8 place-items-center rounded-lg text-mid transition-colors hover:bg-white/10 hover:text-hi disabled:opacity-30 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
